import React from 'react'
import { Html } from '@react-three/drei'
import { Vector3 } from 'three'
import { useDesignStore } from '../../store/designStore'

interface MeasurementLineProps {
  start: Vector3
  end: Vector3
  label: string
  color?: string
}

const MeasurementLine: React.FC<MeasurementLineProps> = ({ 
  start, 
  end, 
  label, 
  color = '#ff6b00' 
}) => {
  const direction = end.clone().sub(start)
  const length = direction.length()
  const midPoint = start.clone().add(direction.clone().multiplyScalar(0.5))
  
  // Create line geometry points
  const points = [start, end]
  
  return (
    <group>
      {/* Main measurement line */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={points.length}
            array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
      
      {/* Start marker */}
      <mesh position={start}>
        <sphereGeometry args={[0.1]} />
        <meshBasicMaterial color={color} />
      </mesh>
      
      {/* End marker */}
      <mesh position={end}>
        <sphereGeometry args={[0.1]} />
        <meshBasicMaterial color={color} />
      </mesh>
      
      {/* Label */}
      <Html position={midPoint} center>
        <div style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          border: `1px solid ${color}`,
        }}>
          {label}
        </div>
      </Html>
    </group>
  )
}

interface ObjectDimensionsProps {
  objectId: string
  showDimensions: boolean
}

const ObjectDimensions: React.FC<ObjectDimensionsProps> = ({ objectId, showDimensions }) => {
  const { getAllObjects } = useDesignStore()
  const objects = getAllObjects()
  const obj = objects.find(o => o.id === objectId)
  
  if (!obj || !showDimensions) return null
  
  const [x, y, z] = obj.position
  const position = new Vector3(x, y, z)
  
  if (obj.type === 'ADU') {
    const { width, height, depth } = obj.dimensions
    
    // Safety check for dimensions
    if (!width || !height || !depth) {
      console.warn('ADU dimensions are missing:', obj.dimensions)
      return null
    }
    
    // Width measurement (X-axis)
    const widthStart = position.clone().add(new Vector3(-width/2, height + 1, 0))
    const widthEnd = position.clone().add(new Vector3(width/2, height + 1, 0))
    
    // Depth measurement (Z-axis)
    const depthStart = position.clone().add(new Vector3(width/2 + 1, height/2, -depth/2))
    const depthEnd = position.clone().add(new Vector3(width/2 + 1, height/2, depth/2))
    
    // Height measurement (Y-axis)
    const heightStart = position.clone().add(new Vector3(-width/2 - 1, 0, 0))
    const heightEnd = position.clone().add(new Vector3(-width/2 - 1, height, 0))
    
    return (
      <group>
        <MeasurementLine 
          start={widthStart} 
          end={widthEnd} 
          label={`${width.toFixed(1)}ft`}
          color="#00ff00"
        />
        <MeasurementLine 
          start={depthStart} 
          end={depthEnd} 
          label={`${depth.toFixed(1)}ft`}
          color="#0080ff"
        />
        <MeasurementLine 
          start={heightStart} 
          end={heightEnd} 
          label={`${height.toFixed(1)}ft`}
          color="#ff6b00"
        />
      </group>
    )
  }
  
  if (obj.type === 'Pool') {
    const { width, length, depth } = obj.dimensions
    
    // Safety check for dimensions
    if (!width || !length || !depth) {
      console.warn('Pool dimensions are missing:', obj.dimensions)
      return null
    }
    
    // Width measurement (X-axis)
    const widthStart = position.clone().add(new Vector3(-width/2, 2, 0))
    const widthEnd = position.clone().add(new Vector3(width/2, 2, 0))
    
    // Length measurement (Z-axis)
    const lengthStart = position.clone().add(new Vector3(width/2 + 1, 1, -length/2))
    const lengthEnd = position.clone().add(new Vector3(width/2 + 1, 1, length/2))
    
    // Depth measurement (Y-axis, going down)
    const depthStart = position.clone().add(new Vector3(-width/2 - 1, 0, 0))
    const depthEnd = position.clone().add(new Vector3(-width/2 - 1, -depth, 0))
    
    return (
      <group>
        <MeasurementLine 
          start={widthStart} 
          end={widthEnd} 
          label={`${width.toFixed(1)}ft`}
          color="#00ff00"
        />
        <MeasurementLine 
          start={lengthStart} 
          end={lengthEnd} 
          label={`${length.toFixed(1)}ft`}
          color="#0080ff"
        />
        <MeasurementLine 
          start={depthStart} 
          end={depthEnd} 
          label={`${depth.toFixed(1)}ft deep`}
          color="#ff6b00"
        />
      </group>
    )
  }
  
  return null
}

interface MeasurementToolsProps {
  showMeasurements?: boolean
}

const MeasurementTools: React.FC<MeasurementToolsProps> = ({ showMeasurements = false }) => {
  const { selectedObjects, getAllObjects } = useDesignStore()
  const allObjects = getAllObjects()
  
  if (!showMeasurements) return null
  
  return (
    <group>
      {/* Show dimensions for selected objects */}
      {selectedObjects.map(obj => (
        <ObjectDimensions 
          key={obj.id} 
          objectId={obj.id} 
          showDimensions={true}
        />
      ))}
      
      {/* Show distance between objects if multiple selected */}
      {selectedObjects.length === 2 && (
        <MeasurementLine
          start={new Vector3(...selectedObjects[0].position)}
          end={new Vector3(...selectedObjects[1].position)}
          label={`${new Vector3(...selectedObjects[0].position)
            .distanceTo(new Vector3(...selectedObjects[1].position))
            .toFixed(1)}ft apart`}
          color="#ff0080"
        />
      )}
      
      {/* Property boundary indicators */}
      <group>
        {/* Property lines - 50ft x 50ft lot */}
        <line>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={5}
              array={new Float32Array([
                -25, 0, -25,  // Bottom-left
                25, 0, -25,   // Bottom-right
                25, 0, 25,    // Top-right
                -25, 0, 25,   // Top-left
                -25, 0, -25   // Close the loop
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#666666" linewidth={1} transparent opacity={0.3} />
        </line>
        
        {/* Property dimension labels */}
        <Html position={new Vector3(0, 0, -26)} center>
          <div style={{
            color: '#666',
            fontSize: '10px',
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            50ft
          </div>
        </Html>
        
        <Html position={new Vector3(-26, 0, 0)} center>
          <div style={{
            color: '#666',
            fontSize: '10px',
            textAlign: 'center',
            pointerEvents: 'none',
            transform: 'rotate(-90deg)'
          }}>
            50ft
          </div>
        </Html>
      </group>
    </group>
  )
}

export default MeasurementTools
